/**
 * The words an apply's result is reported in (plan 2026-10-15, Tasks 3 and 4). Pure, so the toast and
 * its tests read one source.
 */
import { longDate } from './backfillWindow'
import type { Applied, AppliedAll } from './useRuleApply'

/** "5,000": counts the user reads are grouped, as the plan's copy writes them. */
export const count = (n: number) => n.toLocaleString('en-US')

const transactions = (n: number) => `${count(n)} transaction${n === 1 ? '' : 's'}`

/**
 * Where a run the server cut short stopped. Over a window, Apply now continues from the server's
 * cursor (`resume_after`, backend 521), so this says how far it got and to Continue; `processed` is
 * what it reached: the 5,000 cap, or fewer when the server's time budget ran out. Never "at least":
 * the count changed is exact for the run. Chosen ids have nothing older to continue to, and a run
 * the server gave no cursor cannot be continued, so neither is offered it.
 */
function stoppedAt(a: Applied): string {
  if (a.from === null) return `Stopped after ${transactions(a.processed)} of the ones you chose. The rest weren't reached.`
  // No cursor, so no Continue to offer: say where it stopped and no more.
  if (a.resumeAfter === null) return `Stopped after the newest ${transactions(a.processed)} since ${longDate(a.from)}. Older ones weren't reached.`
  return stoppedContinue(a.processed)
}

/** Apply now's cut-short line, in the toast and in the dialog that stays up to continue. */
export const stoppedContinue = (processed: number) => `Stopped after ${count(processed)}. Continue to check older transactions.`

/** A continue whose cursor the server refused (422): it is not retried, the user starts over. */
export const CONTINUE_FAILED = "Couldn't continue. Start again from the beginning."

/**
 * The toast after Apply now or Apply to selected.
 *
 * - The count changed is exact. A run the server cut short (`truncated`) adds where it stopped
 *   (`stoppedAt`), so it is never read as complete.
 * - On a fill-only apply a field the user edited by hand is not written (`respect_user_edits`), and
 *   when that left any match alone the toast says how many, so "changed 3" beside 5 ticked is
 *   explained. An overwrite writes them (owner ruling 2026-10-15), so the server reports none.
 */
export function appliedMessage(name: string, a: Applied): string {
  const changed = a.modified === 0 ? `“${name}” didn't change anything.` : `“${name}” changed ${transactions(a.modified)}.`
  const lead = a.truncated ? `${changed} ${stoppedAt(a)}` : changed
  if (a.skippedLocked === 0) return lead
  return `${lead} ${count(a.skippedLocked)} you edited by hand ${a.skippedLocked === 1 ? 'was' : 'were'} left alone.`
}

/** An apply that failed. A paused rule is refused with a 400 (the page never offers it, but another
 *  device may have paused it since); a deleted one with a 404, which also refreshes the page's rule. */
export const applyFailed = (status: number | undefined) =>
  status === 400
    ? "Couldn't apply this rule. It may have been turned off on another device. Nothing was changed."
    : status === 404
      ? 'This rule no longer exists. It may have been deleted on another device. Nothing was changed.'
      : "Couldn't apply this rule. Nothing was changed. Try again in a moment."

/** Apply all rules' toast, one per run. A run cut short says "at least"; the dialog stays up to
 *  continue it. */
export function appliedAllMessage(a: AppliedAll): string {
  if (a.modified === 0 && !a.atLeast) return "Your rules didn't change anything."
  return `Your rules changed ${a.atLeast ? 'at least ' : ''}${transactions(a.modified)}.`
}

/** Apply all rules that failed. 409: another run for this account is still going. */
export const applyAllFailed = (status: number | undefined) =>
  status === 409
    ? 'Rules are already being applied. Try again in a minute.'
    : "Couldn't apply your rules. Nothing was changed. Try again in a moment."
